/**
 * R2LiveWallpaper — Engineering Operations Live Background
 * Canvas-based animated wallpaper: circuit nodes, data streams,
 * blueprint grid, hex cells, scan lines, technical annotations.
 * Runs as a fixed z=0 layer behind all app content.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Node { x: number; y: number; vx: number; vy: number; r: number; pulse: number; pulseSpd: number; color: string; }
interface Particle { x: number; y: number; progress: number; speed: number; fromIdx: number; toIdx: number; }
interface HexCell { cx: number; cy: number; r: number; opacity: number; phase: number; phaseSpd: number; }
interface ScanLine { y: number; speed: number; opacity: number; }

// ── R2 color palette ──────────────────────────────────────────────────────────
const TEAL   = '37,181,168';
const PURPLE = '139,92,246';
const AMBER  = '245,158,11';
const CYAN   = '34,211,238';

function randColor(): string {
  const pick = Math.random();
  if (pick < 0.65) return TEAL;
  if (pick < 0.82) return PURPLE;
  if (pick < 0.92) return CYAN;
  return AMBER;
}

function hexPoints(cx: number, cy: number, r: number): [number,number][] {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number,number];
  });
}

function drawHex(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, opacity: number, color: string) {
  const pts = hexPoints(cx, cy, r);
  ctx.beginPath();
  ctx.moveTo(pts[0]?.[0] ?? 0, pts[0]?.[1] ?? 0);
  for (let i = 1; i < 6; i++) ctx.lineTo(pts[i]?.[0] ?? 0, pts[i]?.[1] ?? 0);
  ctx.closePath();
  ctx.strokeStyle = `rgba(${color},${opacity})`;
  ctx.lineWidth = 0.6;
  ctx.stroke();
}

const NODE_COUNT = 32;
const PARTICLE_COUNT = 40;
const HEX_COUNT = 22;
const SCANLINE_COUNT = 4;
const ANNOTATION_TEXTS = [
  'SYS.OK','INIT','READY','R2.ENGINE','BIM.CORE','AI.ACTIVE',
  'NODE.42','DATA.OK','SYNC','LIVE','0xFF','0xAA','SECURE',
  '100ms','3D.CORE','IFC','LOD-3','BIM.IQ','RCDD','VERIFIED',
];

export function R2LiveWallpaper() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    let w = window.innerWidth;
    let h = window.innerHeight;
    let raf: number;

    // ── State ────────────────────────────────────────────────────────────────
    let nodes: Node[] = [];
    let particles: Particle[] = [];
    let hexCells: HexCell[] = [];
    let scanLines: ScanLine[] = [];
    let annotations: { x: number; y: number; text: string; opacity: number; phase: number; phaseSpd: number }[] = [];

    function init(width: number, height: number) {
      // Nodes
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 2 + 1.5,
        pulse: Math.random() * Math.PI * 2,
        pulseSpd: 0.01 + Math.random() * 0.025,
        color: randColor(),
      }));

      // Particles — pre-assign connections
      particles = Array.from({ length: PARTICLE_COUNT }, () => {
        const fromIdx = Math.floor(Math.random() * NODE_COUNT);
        const toIdx   = (fromIdx + 1 + Math.floor(Math.random() * 8)) % NODE_COUNT;
        return { x: nodes[fromIdx]?.x ?? 0, y: nodes[fromIdx]?.y ?? 0, progress: Math.random(), speed: 0.003 + Math.random() * 0.007, fromIdx, toIdx };
      });

      // Hex cells — scattered across the canvas
      hexCells = Array.from({ length: HEX_COUNT }, () => ({
        cx: Math.random() * width,
        cy: Math.random() * height,
        r: 30 + Math.random() * 60,
        opacity: 0.03 + Math.random() * 0.05,
        phase: Math.random() * Math.PI * 2,
        phaseSpd: 0.005 + Math.random() * 0.008,
      }));

      // Scan lines
      scanLines = Array.from({ length: SCANLINE_COUNT }, () => ({
        y: Math.random() * height,
        speed: 0.25 + Math.random() * 0.5,
        opacity: 0.025 + Math.random() * 0.035,
      }));

      // Technical annotations
      annotations = Array.from({ length: 18 }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        text: ANNOTATION_TEXTS[i % ANNOTATION_TEXTS.length] ?? '',
        opacity: 0.06 + Math.random() * 0.1,
        phase: Math.random() * Math.PI * 2,
        phaseSpd: 0.008 + Math.random() * 0.012,
      }));
    }

    function resize() {
      w = window.innerWidth;
      h = window.innerHeight;
      if (!canvas) return;
      canvas.width  = w;
      canvas.height = h;
      init(w, h);
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);

      // ── 1. Blueprint engineering grid ──────────────────────────────────────
      ctx.lineWidth = 0.5;
      const GRID = 44;
      ctx.strokeStyle = `rgba(${TEAL},0.04)`;
      ctx.beginPath();
      for (let x = 0; x < w; x += GRID) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
      for (let y = 0; y < h; y += GRID) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
      ctx.stroke();

      // Major grid every 5 lines
      ctx.strokeStyle = `rgba(${TEAL},0.07)`;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      for (let x = 0; x < w; x += GRID * 5) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
      for (let y = 0; y < h; y += GRID * 5) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
      ctx.stroke();

      // ── 2. Hex cells ────────────────────────────────────────────────────────
      hexCells.forEach((hx) => {
        hx.phase += hx.phaseSpd;
        const op = hx.opacity * (0.6 + 0.4 * Math.sin(hx.phase));
        const c = Math.random() > 0.98 ? CYAN : TEAL;
        drawHex(ctx, hx.cx, hx.cy, hx.r, op, c);
      });

      // ── 3. Node connections ─────────────────────────────────────────────────
      ctx.lineWidth = 0.7;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 220) {
            const alpha = (1 - dist / 220) * 0.12;
            const pulse = 0.6 + 0.4 * Math.sin(a.pulse);
            ctx.strokeStyle = `rgba(${a.color},${(alpha * pulse).toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // ── 4. Scan lines ───────────────────────────────────────────────────────
      scanLines.forEach((sl) => {
        sl.y = (sl.y + sl.speed) % h;
        const grad = ctx.createLinearGradient(0, sl.y, w, sl.y);
        grad.addColorStop(0,   `rgba(${TEAL},0)`);
        grad.addColorStop(0.4, `rgba(${TEAL},${sl.opacity})`);
        grad.addColorStop(0.6, `rgba(${TEAL},${sl.opacity})`);
        grad.addColorStop(1,   `rgba(${TEAL},0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, sl.y - 1, w, 2);
      });

      // ── 5. Data stream particles ─────────────────────────────────────────────
      particles.forEach((p) => {
        p.progress += p.speed;
        if (p.progress >= 1) {
          p.progress = 0;
          p.fromIdx = p.toIdx;
          p.toIdx = (p.fromIdx + 1 + Math.floor(Math.random() * 8)) % NODE_COUNT;
        }
        const from = nodes[p.fromIdx]!;
        const to   = nodes[p.toIdx]!;
        if (!from || !to) return;
        p.x = from.x + (to.x - from.x) * p.progress;
        p.y = from.y + (to.y - from.y) * p.progress;

        const brightness = 1 - Math.abs(p.progress - 0.5) * 1.2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${TEAL},${(brightness * 0.85).toFixed(2)})`;
        ctx.fill();

        // Glow
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 6);
        g.addColorStop(0, `rgba(${TEAL},${(brightness * 0.3).toFixed(2)})`);
        g.addColorStop(1, `rgba(${TEAL},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── 6. Nodes ──────────────────────────────────────────────────────────
      nodes.forEach((n) => {
        n.pulse += n.pulseSpd;
        n.x = (n.x + n.vx + w) % w;
        n.y = (n.y + n.vy + h) % h;

        const glow = 0.5 + 0.5 * Math.sin(n.pulse);

        // Outer glow
        const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 5);
        g.addColorStop(0, `rgba(${n.color},${(0.18 * glow).toFixed(3)})`);
        g.addColorStop(1, `rgba(${n.color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 5, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${n.color},${(0.7 + 0.3 * glow).toFixed(2)})`;
        ctx.fill();

        // Crosshair tick marks on larger nodes
        if (n.r > 2.5) {
          ctx.strokeStyle = `rgba(${n.color},${(0.3 * glow).toFixed(2)})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(n.x - n.r * 3, n.y);
          ctx.lineTo(n.x - n.r * 1.5, n.y);
          ctx.moveTo(n.x + n.r * 1.5, n.y);
          ctx.lineTo(n.x + n.r * 3, n.y);
          ctx.moveTo(n.x, n.y - n.r * 3);
          ctx.lineTo(n.x, n.y - n.r * 1.5);
          ctx.moveTo(n.x, n.y + n.r * 1.5);
          ctx.lineTo(n.x, n.y + n.r * 3);
          ctx.stroke();
        }
      });

      // ── 7. Technical annotations ──────────────────────────────────────────
      ctx.font = '9px "Courier New", monospace';
      annotations.forEach((a) => {
        a.phase += a.phaseSpd;
        const op = a.opacity * (0.5 + 0.5 * Math.sin(a.phase));
        ctx.fillStyle = `rgba(${TEAL},${op.toFixed(3)})`;
        ctx.fillText(a.text, a.x, a.y);
        // Small bracket before text
        ctx.fillStyle = `rgba(${TEAL},${(op * 0.5).toFixed(3)})`;
        ctx.fillText('[', a.x - 8, a.y);
        ctx.fillText(']', a.x + ctx.measureText(a.text).width + 2, a.y);
      });

      // ── 8. Corner accent overlays (technical HUD feel) ────────────────────
      const cornerSize = 28;
      const cornerOp = 0.14;
      ctx.strokeStyle = `rgba(${TEAL},${cornerOp})`;
      ctx.lineWidth = 1.2;
      // Top-left
      ctx.beginPath(); ctx.moveTo(0, cornerSize); ctx.lineTo(0, 0); ctx.lineTo(cornerSize, 0); ctx.stroke();
      // Top-right
      ctx.beginPath(); ctx.moveTo(w - cornerSize, 0); ctx.lineTo(w, 0); ctx.lineTo(w, cornerSize); ctx.stroke();
      // Bottom-left
      ctx.beginPath(); ctx.moveTo(0, h - cornerSize); ctx.lineTo(0, h); ctx.lineTo(cornerSize, h); ctx.stroke();
      // Bottom-right
      ctx.beginPath(); ctx.moveTo(w - cornerSize, h); ctx.lineTo(w, h); ctx.lineTo(w, h - cornerSize); ctx.stroke();

      raf = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return createPortal(
    <canvas
      ref={canvasRef}
      data-testid="r2-live-wallpaper"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 1,
      }}
    />,
    document.body,
  );
}
